import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Flex, Space, Spin, Tag, Typography } from 'antd';
import { DiffOutlined } from '@ant-design/icons';
import { ObjectCardSection } from '../../../components/layout/ObjectCard';
import { useTranslation } from 'react-i18next';
import {
  fetchInvoiceCorrectionState,
  invoiceWorkflowApi,
  type InvoiceCorrectionSummary,
} from '../api/invoiceWorkflowApi';
import type { InvoiceCorrectionState } from '../../../dto/invoiceCorrection';
import { isAmountInvoice, type WorkflowInvoice, type WorkflowInvoiceDirection } from '../../../types/workflowInvoice';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { formatRemainderLinesInSentence } from '../invoiceCoverageLabels';
import { useFeedback } from '../../../utils/feedback';
import { formatMoney } from '../../../utils/money';
import { formatUnit } from '../../../utils/units';
import InvoiceComplaintEditor, { formatComplaintLines } from './InvoiceComplaintEditor';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import InvoiceWithdrawModal from './InvoiceWithdrawModal';

interface InvoiceCorrectionsPanelProps {
  direction: WorkflowInvoiceDirection;
  invoice: WorkflowInvoice;
  /** Approval preview of a correction draft: net before/after in PLN. */
  preview?: InvoiceCorrectionSummary | null;
  /** Open another invoice (a correction draft, the original) on this screen. */
  onOpen: (invoiceId: string) => void;
  /** The invoice or its order changed (withdrawal): reload the card. */
  onChanged: () => void;
  /** WP15: unsaved invoice edits lock the complaint save (it bumps the version). */
  locked?: boolean;
  /** Routed invoice card: an `ObjectCardSection` (`wide` spans both columns) instead of the antd Card. */
  section?: { wide: boolean };
}

const pln = (value: number | null | undefined) => (value == null ? '—' : `${formatMoney(value, 'pl')} PLN`);

/**
 * WP2 (docs/decisions/2026-09-24-wp2-korekty-faktur.md): correction invoices
 * and withdrawal on the invoice card. A correction is entered from outside
 * like the original (manual or OCR) and states the invoice after the
 * correction; only the difference reaches the result, in the correction's
 * month. Withdrawal (administrator, with a reason) reverses what the invoice
 * recognized; the invoice itself stays in history.
 */
export default function InvoiceCorrectionsPanel({ direction, invoice, preview, onOpen, onChanged, locked = false, section }: InvoiceCorrectionsPanelProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const feedback = useFeedback();
  const [state, setState] = useState<InvoiceCorrectionState | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const invoiceId = invoice.id;

  const load = useCallback(() => {
    if (!invoiceId) return undefined;
    let active = true;
    setFailed(false);
    fetchInvoiceCorrectionState(direction, invoiceId)
      .then((value) => { if (active) setState(value); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [direction, invoiceId]);
  useEffect(() => load(), [load, invoice.version, invoice.status]);

  // Advance / settlement invoice: no correction, only the withdrawal (decision 2026-10-20).
  const titleKey = isAmountInvoice(invoice) ? 'invoices:workflow.amountInvoice.withdrawTitle' : 'invoices:workflow.corrections.title';
  const hintKey = isAmountInvoice(invoice) ? 'invoices:workflow.amountInvoice.withdrawHint' : 'invoices:workflow.corrections.hint';
  const frame = (children: React.ReactNode, title = t(titleKey)) => (section
    ? <ObjectCardSection wide={section.wide} testId="invoice-corrections-section" tone="orange" icon={<DiffOutlined />} title={title}>{children}</ObjectCardSection>
    : <>{children}</>);
  if (!invoiceId) return null;
  if (failed) {
    return frame(<Alert type="warning" showIcon message={t('invoices:workflow.corrections.loadFailed')}
      action={<Button onClick={() => load()}>{t('common:refresh')}</Button>} />);
  }
  if (!state) return frame(<Spin size="small" />);

  const createCorrection = async () => {
    setBusy(true);
    try {
      const draft = await invoiceWorkflowApi(direction).createCorrection(invoiceId);
      feedback.success(t('invoices:workflow.corrections.created'));
      if (draft.id) onOpen(draft.id);
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.corrections.createFailed'));
    } finally {
      setBusy(false);
    }
  };
  if (state.isCorrection) {
    return frame(
      <Alert
        type="info"
        showIcon
        data-testid="invoice-correction-banner"
        message={t('invoices:workflow.corrections.draftTitle', { number: state.original?.number || '' })}
        description={<Space direction="vertical" size={4}>
          <span>{t('invoices:workflow.corrections.draftHint')}</span>
          {preview && <span data-testid="invoice-correction-delta">{t('invoices:workflow.corrections.delta', {
            before: pln(preview.netBeforePLN), after: pln(preview.netAfterPLN), delta: pln(preview.netDeltaPLN),
          })}</span>}
          {state.original && <Button type="link" className="invoice-inline-link" onClick={() => onOpen(state.original!.id)}>
            {t('invoices:workflow.corrections.openOriginal')}
          </Button>}
          {/* WP15: „Powód korekty” — ordinary correction or „Reklamacja”. */}
          <InvoiceComplaintEditor direction={direction} invoice={invoice} lines={state.invoiceCoverage?.lines ?? null}
            editable={state.canCorrect} locked={locked} onSaved={onChanged} />
        </Space>}
      />,
      t('invoices:workflow.card.correctionSection'),
    );
  }

  // Section (routed card): outline first, the one primary last, in the header of a wide section.
  const correctButton = state.canCorrect && <Button type="primary" size={section?.wide ? 'small' : undefined} loading={busy} onClick={() => void createCorrection()} data-testid="invoice-create-correction">
    {t('invoices:workflow.corrections.create')}
  </Button>;
  const withdrawButton = state.canWithdraw && <Button danger size={section?.wide ? 'small' : undefined} disabled={busy} onClick={() => setWithdrawOpen(true)} data-testid="invoice-withdraw">
    {t('invoices:workflow.corrections.withdraw')}
  </Button>;
  const actions = state.canCorrect || state.canWithdraw;
  if (section) {
    // A half-width section (paired with „Rzeczywiste przyjęcie towaru”) has no
    // room for the title and two buttons in one header row at 1440 px: the
    // buttons then close the section, right-aligned, outline first.
    const headerActions = actions && section.wide;
    return (
      <ObjectCardSection wide={section.wide} testId="invoice-corrections-panel" tone="orange" icon={<DiffOutlined />}
        title={t(titleKey)} hint={state.withdrawal ? undefined : t(hintKey)} actions={headerActions ? <>{withdrawButton}{correctButton}</> : undefined}>
      <Flex vertical gap={12}>
        {state.withdrawal && <Alert type="warning" showIcon data-testid="invoice-withdrawn"
          message={t('invoices:workflow.corrections.withdrawnTitle', { month: state.withdrawal.recognitionMonth })}
          description={t('invoices:workflow.corrections.withdrawnHint', {
            reason: state.withdrawal.reason, name: state.withdrawal.withdrawnByName || '—',
          })} />}
        {!section && !state.withdrawal && <Typography.Text type="secondary">{t(hintKey)}</Typography.Text>}
        {state.corrections.length > 0 && <Flex vertical gap={4}>
          {state.corrections.map((row) => (
            <Space key={row.id} wrap>
              <Button type="link" className="invoice-inline-link" onClick={() => onOpen(row.id)}>
                {row.number || t('invoices:workflow.corrections.noNumber')}
              </Button>
              <Tag color={row.status === 'APPROVED' ? 'green' : 'gold'}>
                {t(`invoices:workflow.corrections.status.${row.status === 'APPROVED' ? 'approved' : 'draft'}`)}
              </Tag>
              {row.reason === 'COMPLAINT' && <Tag color="volcano" data-testid="invoice-correction-complaint-tag">
                {t('invoices:workflow.corrections.complaintTag')}
              </Tag>}
              {row.recognitionMonth && <Typography.Text type="secondary">{row.recognitionMonth}</Typography.Text>}
              <Typography.Text>{pln(row.netPLN)}</Typography.Text>
              {row.reason === 'COMPLAINT' && row.complaintLines.length > 0 && <Typography.Text type="secondary">
                {t('invoices:workflow.corrections.complaintSummary', {
                  lines: formatComplaintLines(row.complaintLines, state.invoiceCoverage?.lines ?? null),
                })}
              </Typography.Text>}
            </Space>
          ))}
          {state.effectiveInvoiceId && state.effectiveInvoiceId !== state.original?.id && (
            <Typography.Text strong data-testid="invoice-effective-net">
              {t('invoices:workflow.corrections.effective', { amount: pln(state.effectiveNetPLN) })}
            </Typography.Text>
          )}
        </Flex>}
        {state.uninvoicedLines.length > 0 && <Alert type="info" showIcon data-testid="invoice-uninvoiced-lines"
          message={t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'uninvoicedPurchase' : 'uninvoicedSales'}`, {
            lines: formatRemainderLinesInSentence(state.uninvoicedLines),
          })} />}
        {state.issuedOverInvoice.length > 0 && <Alert type="warning" showIcon data-testid="invoice-issued-over-invoice"
          message={t('invoices:workflow.corrections.issuedOverInvoice', {
            lines: state.issuedOverInvoice.map((line) => `${line.name} (${formatLocalizedQuantity(line.quantity)} ${formatUnit(line.unit)})`).join(', '),
          })} />}
        {state.openResettlementEntries > 0 && <Alert type="info" showIcon
          message={t('invoices:workflow.corrections.openEntries', { count: state.openResettlementEntries })} />}
        {actions && !headerActions && <Flex justify="flex-end" gap={8}>{withdrawButton}{correctButton}</Flex>}
      </Flex>
      <InvoiceWithdrawModal open={withdrawOpen} direction={direction} invoiceId={invoiceId} number={state.original?.number || null}
        suggestedMonth={state.withdrawalMonthSuggestion}
        onClose={() => setWithdrawOpen(false)} onWithdrawn={() => { onChanged(); load(); }} />
      </ObjectCardSection>
    );
  }
  return (
    <Card size="small" title={t(titleKey)} data-testid="invoice-corrections-panel">
      <Flex vertical gap={12}>
        {state.withdrawal && <Alert type="warning" showIcon data-testid="invoice-withdrawn"
          message={t('invoices:workflow.corrections.withdrawnTitle', { month: state.withdrawal.recognitionMonth })}
          description={t('invoices:workflow.corrections.withdrawnHint', {
            reason: state.withdrawal.reason, name: state.withdrawal.withdrawnByName || '—',
          })} />}
        {!section && !state.withdrawal && <Typography.Text type="secondary">{t(hintKey)}</Typography.Text>}
        {state.corrections.length > 0 && <Flex vertical gap={4}>
          {state.corrections.map((row) => (
            <Space key={row.id} wrap>
              <Button type="link" className="invoice-inline-link" onClick={() => onOpen(row.id)}>
                {row.number || t('invoices:workflow.corrections.noNumber')}
              </Button>
              <Tag color={row.status === 'APPROVED' ? 'green' : 'gold'}>
                {t(`invoices:workflow.corrections.status.${row.status === 'APPROVED' ? 'approved' : 'draft'}`)}
              </Tag>
              {row.reason === 'COMPLAINT' && <Tag color="volcano" data-testid="invoice-correction-complaint-tag">
                {t('invoices:workflow.corrections.complaintTag')}
              </Tag>}
              {row.recognitionMonth && <Typography.Text type="secondary">{row.recognitionMonth}</Typography.Text>}
              <Typography.Text>{pln(row.netPLN)}</Typography.Text>
              {row.reason === 'COMPLAINT' && row.complaintLines.length > 0 && <Typography.Text type="secondary">
                {t('invoices:workflow.corrections.complaintSummary', {
                  lines: formatComplaintLines(row.complaintLines, state.invoiceCoverage?.lines ?? null),
                })}
              </Typography.Text>}
            </Space>
          ))}
          {state.effectiveInvoiceId && state.effectiveInvoiceId !== state.original?.id && (
            <Typography.Text strong data-testid="invoice-effective-net">
              {t('invoices:workflow.corrections.effective', { amount: pln(state.effectiveNetPLN) })}
            </Typography.Text>
          )}
        </Flex>}
        {state.uninvoicedLines.length > 0 && <Alert type="info" showIcon data-testid="invoice-uninvoiced-lines"
          message={t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'uninvoicedPurchase' : 'uninvoicedSales'}`, {
            lines: formatRemainderLinesInSentence(state.uninvoicedLines),
          })} />}
        {state.issuedOverInvoice.length > 0 && <Alert type="warning" showIcon data-testid="invoice-issued-over-invoice"
          message={t('invoices:workflow.corrections.issuedOverInvoice', {
            lines: state.issuedOverInvoice.map((line) => `${line.name} (${formatLocalizedQuantity(line.quantity)} ${formatUnit(line.unit)})`).join(', '),
          })} />}
        {state.openResettlementEntries > 0 && <Alert type="info" showIcon
          message={t('invoices:workflow.corrections.openEntries', { count: state.openResettlementEntries })} />}
        {!section && actions && <Space wrap>{correctButton}{withdrawButton}</Space>}
      </Flex>
      <InvoiceWithdrawModal open={withdrawOpen} direction={direction} invoiceId={invoiceId} number={state.original?.number || null}
        suggestedMonth={state.withdrawalMonthSuggestion}
        onClose={() => setWithdrawOpen(false)} onWithdrawn={() => { onChanged(); load(); }} />
    </Card>
  );
}
